import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "@/contexts/authContext";
import { supabase } from "@/supabase/client";
import Modal from "@/components/ui/Modal";

const DetailsModal = ({ isOpen, onClose, data }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highestGoldUser, setHighestGoldUser] = useState(null);
  const [highestRubyUser, setHighestRubyUser] = useState(null);
  const [highestSapphireUser, setHighestSapphireUser] = useState(null);
  const { user: givenUser, walletAddress } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      if (data.type === "mxs") {
        fetchMXSData();
      } else {
        fetchData();
      }
    }
  }, [isOpen, data.type, walletAddress]);

  const fetchMXSData = async () => {
    try {
      // Fetch user with highest gold
      const { data: highestGold, error: highestGoldError } = await supabase
        .from("seasons_diff")
        .select("*")
        .order("gold", { ascending: false })
        .limit(1)
        .single();
      if (highestGoldError) {
        console.error("Error fetching highest gold user:", highestGoldError);
        return;
      }
      setHighestGoldUser(highestGold);

      // Fetch user with highest ruby
      const { data: highestRuby, error: highestRubyError } = await supabase
        .from("seasons_diff")
        .select("*")
        .order("ruby", { ascending: false })
        .limit(1)
        .single();
      if (highestRubyError) {
        console.error("Error fetching highest ruby user:", highestRubyError);
        return;
      }
      setHighestRubyUser(highestRuby);

      // Fetch user with highest sapphire
      const { data: highestSapphire, error: highestSapphireError } =
        await supabase
          .from("seasons_diff")
          .select("*")
          .order("sapphire", { ascending: false })
          .limit(1)
          .single();
      if (highestSapphireError) {
        console.error(
          "Error fetching highest sapphire user:",
          highestSapphireError
        );
        return;
      }
      setHighestSapphireUser(highestSapphire);

      // If user is logged in, fetch their data
      if (walletAddress) {
        const { data: userData, error: userError } = await supabase
          .from("seasons_diff")
          .select("*")
          .eq("wallet_lc", walletAddress.toLowerCase())
          .single();
        if (userError) {
          console.error("Error fetching user data:", userError);
        } else if (userData) {
          setLeaderboardData([userData]);
        }
      }
    } catch (error) {
      console.error("Error in fetchMXSData:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMXSValue = (userData) => {
    if (
      !userData ||
      !highestGoldUser ||
      !highestRubyUser ||
      !highestSapphireUser
    )
      return null;
    const goldContribution = (userData.gold / highestGoldUser.gold) * 7000;
    const rubyContribution = (userData.ruby / highestRubyUser.ruby) * 1500;
    const sapphireContribution =
      (userData.sapphire / highestSapphireUser.sapphire) * 1500;
    return {
      goldContribution,
      rubyContribution,
      sapphireContribution,
      total: goldContribution + rubyContribution + sapphireContribution,
    };
  };

  const getTitle = (type) => {
    switch (type) {
      case "gold":
        return "MXS Gold Leaderboard";
      case "ruby":
        return "Ruby Leaderboard";
      case "sapphire":
        return "Sapphire Leaderboard";
      case "mxs":
        return "MXS Value Calculation";
      default:
        return "Details";
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Wallet address copied to clipboard!");
  };

  const truncateWallet = (wallet) => {
    return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
  };

  const renderMXSContent = () => {
    if (!walletAddress) {
      return (
        <div className="p-6 text-center">
          <h3 className="text-xl font-semibold mb-4 text-white">
            Please Log In
          </h3>
          <p className="text-gray-300 mb-4">
            You need to log in to view your MXS value calculation.
          </p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="flex justify-center items-center py-10">
          <svg
            className="animate-spin h-8 w-8 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
            ></path>
          </svg>
        </div>
      );
    }

    const userData = leaderboardData[0];
    const mxsValue = calculateMXSValue(userData);

    if (!mxsValue) {
      return (
        <div className="p-6 text-center text-gray-300">No data available</div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded-lg p-3">
          <h3 className="text-lg font-semibold text-white mb-1">
            What is MXS Points?
          </h3>
          <p className="mb-0 text-white text-sm">
            MXS Points are the core ranking metric in the MXS leaderboard
            system. They determine a player's standing based on performance
            across three categories: Gold (7,000 max), Ruby (1,500 max), and
            Sapphire (1,500 max).
          </p>
        </div>
        <div className="rounded-lg p-3">
          <h3 className="text-lg font-semibold mb-1 text-white">
            MXS Value Overview
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-300 border-2 border-zinc-700 rounded-lg">
              <thead>
                <tr className="bg-metaxseed-blue/30 text-white">
                  <th className="px-4 py-2 text-left border border-gray-700">
                    Category
                  </th>
                  <th className="px-4 py-2 text-center border border-gray-700">
                    Gold
                  </th>
                  <th className="px-4 py-2 text-center border border-gray-700">
                    Ruby
                  </th>
                  <th className="px-4 py-2 text-center border border-gray-700">
                    Sapphire
                  </th>
                  <th className="px-4 py-2 text-center border border-gray-700"></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-2 font-semibold text-white border border-zinc-700">
                    Top Holder
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {highestGoldUser ? highestGoldUser.gold : "-"}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {highestRubyUser ? highestRubyUser.ruby : "-"}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {highestSapphireUser ? highestSapphireUser.sapphire : "-"}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700"></td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-semibold text-white border border-zinc-700">
                    Your Value
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {userData.gold}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {userData.ruby}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {userData.sapphire}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700"></td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-semibold text-white border border-zinc-700">
                    MXS Contribution
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {Math.round(mxsValue.goldContribution)}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {Math.round(mxsValue.rubyContribution)}
                  </td>
                  <td className="px-4 py-2 text-center border border-zinc-700">
                    {Math.round(mxsValue.sapphireContribution)}
                  </td>
                  <td className="px-4 py-2 text-center font-bold text-white border border-zinc-700 bg-gradient-to-r from-orange-600 to-yellow-600">
                    {Math.round(mxsValue.total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="rounded-lg p-3">
          <h3 className="text-lg font-semibold text-white mb-1">
            How Your MXS Value is Calculated
          </h3>
          <div className="space-y-1 text-sm text-gray-300">
            <div className="flex items-start gap-2">
              <Icon icon="mdi:arrow-right" className="text-gray-400 mt-1" />
              <span>
                Gold contribution is:{" "}
                <span className="text-blue-400 font-mono bg-zinc-800 px-1 rounded">
                  (Your Gold / Highest Gold) × 7000
                </span>
              </span>
            </div>
            <div className="flex items-start gap-2">
              <Icon icon="mdi:arrow-right" className="text-gray-400 mt-1" />
              <span>
                Ruby contribution is:{" "}
                <span className="text-blue-400 font-mono bg-zinc-800 px-1 rounded">
                  (Your Ruby / Highest Ruby) × 1500
                </span>
              </span>
            </div>
            <div className="flex items-start gap-2">
              <Icon icon="mdi:arrow-right" className="text-gray-400 mt-1" />
              <span>
                Sapphire contribution is:{" "}
                <span className="text-blue-400 font-mono bg-zinc-800 px-1 rounded">
                  (Your Sapphire / Highest Sapphire) × 1500
                </span>
              </span>
            </div>
            <div className="flex items-start gap-2">
              <Icon icon="mdi:arrow-right" className="text-gray-400 mt-1" />
              <span>Total MXS Value is the sum of all contributions</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const fetchData = async () => {
    try {
      let currentUserData = null;
      // If we have a wallet address, fetch current user data first
      if (walletAddress) {
        const { data: userData, error: userError } = await supabase
          .from("seasons_diff")
          .select("*")
          .eq("wallet_lc", walletAddress.toLowerCase())
          .single();
        if (userError) {
          console.error("Error fetching current user data:", userError);
        } else if (userData) {
          currentUserData = userData;
        }
      }
      // Fetch leaderboard data
      const { data: leaderboard, error: leaderboardError } = await supabase
        .from("seasons_diff")
        .select("*")
        .gt(
          data.type === "gold"
            ? "gold_contribution"
            : data.type === "ruby"
            ? "ruby_contribution"
            : "sapphire_contribution",
          0
        )
        .order(
          data.type === "gold"
            ? "gold_contribution"
            : data.type === "ruby"
            ? "ruby_contribution"
            : "sapphire_contribution",
          { ascending: false }
        )
        .limit(10);
      if (leaderboardError) {
        console.error("Error fetching leaderboard data:", leaderboardError);
        setLoading(false);
        return;
      }
      // Combine the data
      let combinedData = leaderboard;
      if (currentUserData) {
        // Check if current user is already in the leaderboard
        const isUserInLeaderboard = leaderboard.some(
          (item) => item.wallet_lc === currentUserData.wallet_lc
        );
        if (!isUserInLeaderboard) {
          combinedData = [currentUserData, ...leaderboard];
        }
      }
      setLeaderboardData(combinedData);
    } catch (error) {
      console.error("Error in fetchData:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal activeModal={isOpen} onClose={onClose} title={getTitle(data.type)}>
      {data.type === "mxs" ? (
        renderMXSContent()
      ) : (
        <>
          <div className="px-4">
            <span className="font-normal capitalize">
              {data.type === "gold"
                ? "GOLD is accumulated by playing games on the MXS platform."
                : data.type === "ruby"
                ? "RUBY is accumulated by holding XSEED in your wallet."
                : "SAPPHIRE is accumulated by purchasing Node Licenses."}{" "}
              Leaderboard
            </span>
          </div>
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <svg
                className="animate-spin h-8 w-8 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                ></path>
              </svg>
            </div>
          ) : (
            <div className="overflow-x-auto p-4 rounded-lg">
              <table className="min-w-full text-xs text-gray-300 border rounded-md border-zinc-700">
                <thead>
                  <tr className="bg-metaxseed-blue/30 text-white ">
                    <th className="px-4 py-2 text-left border border-gray-700 text-xs">
                      Ranking
                    </th>
                    <th className="px-4 py-2 text-left border border-gray-700 text-xs">
                      Wallet Address
                    </th>
                    <th className="px-4 py-2 text-center border border-gray-700 text-xs">
                      Contribution
                    </th>
                    <th className="px-4 py-2 text-center border border-gray-700 capitalize text-xs">
                      {data.type}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((item, index) => {
                    const isCurrentUser =
                      walletAddress &&
                      item.wallet_lc === walletAddress.toLowerCase();
                    return (
                      <tr
                        key={index}
                        className={
                          isCurrentUser
                            ? "bg-gradient-to-r from-orange-600 to-yellow-600 text-white font-bold border-2 border-zinc-700"
                            : "text-white"
                        }
                      >
                        {/* Ranking */}
                        <td className="px-4 py-2 border border-zinc-700 text-left text-xs">
                          {walletAddress
                            ? index === 0
                              ? `#${
                                  data.type === "gold"
                                    ? item.gold_rank
                                    : data.type === "ruby"
                                    ? item.ruby_rank
                                    : item.sapphire_rank
                                }`
                              : `#${index}`
                            : `#${index + 1}`}
                        </td>
                        {/* Wallet Address */}
                        <td className="px-4 py-2 border border-zinc-700 text-xs">
                          <span className="flex items-center text-xs">
                            <span
                              className={
                                isCurrentUser
                                  ? "text-white text-xs"
                                  : "text-zinc-200 text-xs"
                              }
                            >
                              {truncateWallet(item.wallet)}
                            </span>
                            <Icon
                              icon="mdi:content-copy"
                              className={`ml-1 w-4 h-4 cursor-pointer ${
                                isCurrentUser
                                  ? "text-white hover:text-zinc-200"
                                  : "text-zinc-400 hover:text-zinc-300"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(item.wallet);
                              }}
                            />
                          </span>
                        </td>
                        {/* Contribution */}
                        <td className="px-4 py-2 border border-zinc-700 text-center text-xs">
                          <span
                            className={`inline-flex items-center w-full px-3 py-1 rounded font-semibold text-xs ${
                              isCurrentUser
                                ? "bg-blue-600 text-white"
                                : "bg-zinc-700 text-zinc-200"
                            }`}
                          >
                            <img
                              src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_icon_white.png?alt=media&token=8a7e36ba-954b-46b3-bcb2-559cec885bdf"
                              alt="contribution"
                              className="w-4 h-4 mr-1"
                            />
                            {data.type === "gold"
                              ? Math.floor(item.gold_contribution)
                              : data.type === "ruby"
                              ? Math.floor(item.ruby_contribution)
                              : Math.floor(item.sapphire_contribution)}
                          </span>
                        </td>
                        {/* Type-specific value */}
                        <td className="px-4 py-2 border border-zinc-700 text-center text-xs">
                          <span
                            className={`inline-flex items-center w-full px-3 py-1 rounded font-semibold text-xs ${
                              isCurrentUser
                                ? "bg-zinc-700 text-white"
                                : "bg-zinc-700 text-zinc-200"
                            }`}
                          >
                            {data.type === "ruby" ? (
                              <img
                                src="https://cqfjxolodeqrlvaifybm.supabase.co/storage/v1/object/public/image_gems//gem_4251687.png"
                                alt="coin"
                                className="w-4 h-4 mr-1"
                              />
                            ) : data.type === "sapphire" ? (
                              <img
                                src="https://cqfjxolodeqrlvaifybm.supabase.co/storage/v1/object/public/image_gems//gem_785126.png"
                                alt="coin"
                                className="w-4 h-4 mr-1"
                              />
                            ) : (
                              <img
                                src="https://www.metaxseed.io/_next/image?url=https%3A%2F%2Fres.cloudinary.com%2Fdot2onqdb%2Fimage%2Fupload%2Fv1729068489%2Fgold_lr_h4yg2m.png&w=640&q=75"
                                alt="coin"
                                className="w-4 h-4 mr-1"
                              />
                            )}
                            {item[data.type]}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default DetailsModal;
